import React, { useEffect, useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import ImageIcon from '@material-ui/icons/Image';
import { makeStyles } from '@material-ui/core/styles';

import { saveProfilePicture } from '../imagesApi';
import { colors } from '../theme';

// Правила картинки профиля. Те же числа проверяет сервер. Meta принимает через Resumable
// Upload JPEG и PNG; остальное по описаниям провайдеров Cloud API: квадрат, не меньше 192 px
// (WhatsApp хранит 640 px, так что 640 и больше лучше), до 5 МБ.
const MAX_BYTES = 5 * 1024 * 1024;
const MIN_SIDE = 192;
const GOOD_SIDE = 640;
const TYPES = ['image/png', 'image/jpeg'];

const useStyles = makeStyles({
  row: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 },
  circle: {
    width: 88,
    height: 88,
    borderRadius: '50%',
    overflow: 'hidden',
    flexShrink: 0,
    marginRight: 18,
    backgroundColor: colors.panelAlt,
    border: `1px solid ${colors.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  image: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  placeholder: { color: colors.textFaint, fontSize: '40px' },
  body: { flex: 1, minWidth: 240 },
  title: { color: colors.text, fontWeight: 600, fontSize: '14px', marginBottom: 2 },
  hint: { color: colors.textDim, fontSize: '12px', lineHeight: 1.45, marginBottom: 10 },
  actions: { display: 'flex', alignItems: 'center', flexWrap: 'wrap' },
  fileName: { color: colors.textDim, fontSize: '12px', marginLeft: 12 },
  error: { color: '#d32f2f', fontSize: '12px', marginTop: 8 },
  warn: { color: colors.warnText, fontSize: '12px', marginTop: 8 },
  ok: { color: colors.okText, fontSize: '12px', marginTop: 8 },
});

// Квадратный ли файл, достаточно ли он большой: смотрим размер в браузере до отправки.
const readSize = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
    image.onerror = () => reject(new Error('Не удалось прочитать картинку.'));
    image.src = url;
  });

// Логотип, который клиенты видят аватаркой чата. current: адрес текущей картинки у Meta.
function ProfilePictureEditor({ current, onSaved }) {
  const classes = useStyles();
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [size, setSize] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  const handlePick = async (event) => {
    const picked = event.target.files && event.target.files[0];
    event.target.value = '';
    setDone(false);
    setError('');
    setSize(null);
    setFile(null);
    setPreview('');
    if (!picked) return;

    if (!TYPES.includes(picked.type)) {
      setError('Нужен PNG или JPG.');
      return;
    }
    if (picked.size > MAX_BYTES) {
      setError('Файл больше 5 МБ.');
      return;
    }
    const url = URL.createObjectURL(picked);
    try {
      const found = await readSize(url);
      if (found.width !== found.height) {
        URL.revokeObjectURL(url);
        setError(`Картинка должна быть квадратной, а эта ${found.width}×${found.height}.`);
        return;
      }
      if (found.width < MIN_SIDE) {
        URL.revokeObjectURL(url);
        setError(`Слишком маленькая: ${found.width} px. Нужно не меньше ${MIN_SIDE}, лучше ${GOOD_SIDE}.`);
        return;
      }
      setFile(picked);
      setPreview(url);
      setSize(found);
    } catch (err) {
      URL.revokeObjectURL(url);
      setError(err.message);
    }
  };

  const handleSave = async () => {
    setBusy(true);
    setError('');
    const result = await saveProfilePicture(file);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    setDone(true);
    setFile(null);
    setPreview('');
    setSize(null);
    if (onSaved) onSaved();
  };

  const shown = preview || current;

  return (
    <div className={classes.row}>
      <div className={classes.circle}>
        {shown ? <img className={classes.image} src={shown} alt="Логотип профиля" /> : <ImageIcon className={classes.placeholder} />}
      </div>
      <div className={classes.body}>
        <div className={classes.title}>Логотип компании</div>
        <div className={classes.hint}>
          Это аватарка вашего чата у клиентов, она показывается кругом: важное держите ближе к центру. PNG или JPG,
          квадрат, от {MIN_SIDE} px (лучше {GOOD_SIDE} и больше), до 5 МБ. PNG без прозрачности: прозрачный фон
          WhatsApp рисует чёрным.
        </div>
        <div className={classes.actions}>
          <input ref={input} type="file" accept="image/png,image/jpeg" hidden onChange={handlePick} />
          <Button size="small" variant="outlined" disabled={busy} onClick={() => input.current && input.current.click()}>
            Выбрать файл
          </Button>
          {file && (
            <>
              <Button
                size="small"
                variant="contained"
                color="primary"
                disableElevation
                disabled={busy}
                style={{ marginLeft: 12 }}
                onClick={handleSave}
              >
                {busy ? 'Загружаем…' : 'Загрузить логотип'}
              </Button>
              <span className={classes.fileName}>
                {file.name}, {size.width}×{size.height}
              </span>
            </>
          )}
        </div>
        {size && size.width < GOOD_SIDE && (
          <div className={classes.warn}>
            Подойдёт, но {size.width} px это мало: на экранах с высокой плотностью будет мягко. Лучше {GOOD_SIDE} и больше.
          </div>
        )}
        {error && <div className={classes.error}>{error}</div>}
        {done && !error && (
          <div className={classes.ok}>Логотип отправлен в Meta. WhatsApp может показать его у клиентов не сразу.</div>
        )}
      </div>
    </div>
  );
}

export default ProfilePictureEditor;
